/*
 * Copyright (c) 2026 SimplyBook.me Ltd. All rights reserved.
 * Licensed under the Apache License, Version 2.0.
 */

import {
  addMonths,
  eachDayOfInterval,
  endOfMonth,
  endOfWeek,
  format,
  getDaysInMonth,
  parse,
  startOfMonth,
  startOfWeek,
  subMonths,
} from "date-fns";

/**
 * Parse a "YYYY-MM-DD" string as local midnight using date-fns parse().
*/
export const localDate = (dateStr) => parse(dateStr, "yyyy-MM-dd", new Date());

const YEARS_INTO_PAST = 100;
const YEARS_INTO_FUTURE = 1;

export const generateYearsRange = () => {
  const currentYear = new Date().getFullYear();
  const startYear = currentYear - YEARS_INTO_PAST;
  const endYear = currentYear + YEARS_INTO_FUTURE;

  return Array.from({ length: endYear - startYear + 1 }, (_, index) => startYear + index);
};

export const getDaysInCalendar = (currentMonth, weekStartsOn = 0) => {
  const monthStart = startOfMonth(currentMonth);
  const monthEnd = endOfMonth(monthStart);
  const calendarStart = startOfWeek(monthStart, { weekStartsOn });
  const calendarEnd = endOfWeek(monthEnd, { weekStartsOn });

  return eachDayOfInterval({ start: calendarStart, end: calendarEnd }).map((date) => ({
    date,
    isOutsideMonth: date.getMonth() !== currentMonth.getMonth(),
  }));
};

export const getAvailableDaysInMonth = (date) => {
  const month = date.getMonth();
  const year = date.getFullYear();

  if ((!month && month !== 0) || !year) return Array.from({ length: 31 }, (_, i) => i + 1);

  const daysInSelectedMonth = getDaysInMonth(new Date(year, month));

  return Array.from({ length: daysInSelectedMonth }, (_, i) => i + 1);
};

export const nextMonth = (currentMonth) => addMonths(currentMonth, 1);
export const previousMonth = (currentMonth) => subMonths(currentMonth, 1);

export const getTodayInTimezone = (timezone) => {
  try {
    const parts = new Intl.DateTimeFormat("en-CA", {
      timeZone: timezone,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    }).formatToParts(new Date());

    const year = parts.find((p) => p.type === "year").value;
    const month = parts.find((p) => p.type === "month").value;
    const day = parts.find((p) => p.type === "day").value;

    return `${year}-${month}-${day}`;
  } catch {
    return format(new Date(), "yyyy-MM-dd");
  }
};
